import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { EaDateInput } from "./analytics/EaDateInput";
import { EaVerifyLink } from "./analytics/EaDashboardCard";

const PowerCell = ({ sp, onSaved }) => {
  const [val, setVal] = useState(sp.available_power_kw ?? "");
  useEffect(() => { setVal(sp.available_power_kw ?? ""); }, [sp.available_power_kw]);
  const save = async () => {
    const v = val === "" ? null : Number(String(val).replace(",", "."));
    if (v !== null && (Number.isNaN(v) || v < 0)) return toast.error("Valore non valido");
    try {
      await api.patch(`/ea/supply-points/${sp.id}`, { available_power_kw: v });
      toast.success("Potenza disponibile salvata");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  return (
    <div className="flex items-center gap-1">
      <input value={val} onChange={(e) => setVal(e.target.value)}
        onBlur={() => String(val) !== String(sp.available_power_kw ?? "") && save()}
        className="w-24 px-2 py-1 rounded border border-slate-200 text-sm text-right" placeholder="kW" data-testid={`ea-sp-power-${sp.row_id}`} />
      <span className="text-[10px] text-slate-400">{sp.available_power_kw_source === "MANUAL" ? "manuale" : sp.available_power_kw_source === "REPORT" ? "da Report" : ""}</span>
    </div>
  );
};

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const inp = "h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white";
const EMPTY = { company: "", point: "", year: "", from: "", to: "" };

function Filters({ f, setF, companies, years }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="ea-sp-filters">
      <select value={f.company} onChange={set("company")} className={inp} data-testid="ea-sp-filter-company">
        <option value="">Tutti i clienti</option>{companies.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <input value={f.point} onChange={set("point")} placeholder="Cerca POD / PDR / REMI" className={`${inp} w-48`} data-testid="ea-sp-filter-point" />
      <select value={f.year} onChange={set("year")} className={inp} data-testid="ea-sp-filter-year">
        <option value="">Tutti gli anni</option>{years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <label className="text-[11px] text-slate-500 flex items-center gap-1">Importati dal
        <EaDateInput value={f.from} onChange={(v) => setF({ ...f, from: v })} testid="ea-sp-filter-from" /></label>
      <label className="text-[11px] text-slate-500 flex items-center gap-1">al
        <EaDateInput value={f.to} onChange={(v) => setF({ ...f, to: v })} testid="ea-sp-filter-to" /></label>
      {Object.values(f).some(Boolean) && (
        <button onClick={() => setF(EMPTY)} className="h-8 px-2 text-xs font-semibold text-purple-700 inline-flex items-center gap-1" data-testid="ea-sp-filter-reset"><X size={12} /> Azzera</button>
      )}
    </div>
  );
}

export const EaSupplyPoints = ({ rows, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const companies = useMemo(() => [...new Set(rows.map((r) => r.company_name).filter(Boolean))].sort(), [rows]);
  const years = useMemo(() => [...new Set(rows.map((r) => r.year))].sort((a, b) => b - a), [rows]);
  const list = useMemo(() => rows.filter((r) => {
    const q = f.point.trim().toLowerCase();
    const day = (r.imported_at || "").slice(0, 10);
    return (!f.company || r.company_name === f.company) && (!f.year || String(r.year) === f.year)
      && (!q || [r.identifier, r.remi, r.code].some((x) => (x || "").toLowerCase().includes(q)))
      && (!f.from || day >= f.from) && (!f.to || day <= f.to);
  }), [rows, f]);
  if (!rows.length) return null;
  const nPoints = new Set(rows.map((r) => r.id)).size;
  return (
    <div className="rounded-xl border border-slate-200" data-testid="ea-supply-points">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="ea-sp-toggle">
        <span className="text-sm font-semibold text-slate-700 inline-flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Punti di fornitura importati
        </span>
        <span className="text-xs text-slate-500" data-testid="ea-sp-count">{nPoints} punti · {rows.length} anni importati</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <Filters f={f} setF={setF} companies={companies} years={years} />
          <div className="text-[11px] text-slate-500" data-testid="ea-sp-results">{list.length} righe · dalla più recente importazione</div>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b">
                  <th className="py-2 pr-3">Importato il</th><th className="pr-3">Cliente</th><th className="pr-3">Punto</th><th className="pr-3">REMI</th>
                  <th className="pr-3">Anno</th><th className="pr-3">Mesi cons. / fatt.</th><th className="pr-3">File</th><th className="pr-3">Potenza disponibile</th><th className="pr-3" />
                </tr>
              </thead>
              <tbody>
                {list.map((sp) => (
                  <tr key={sp.row_id} className="border-b border-slate-100" data-testid={`ea-sp-row-${sp.row_id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap text-xs">{fmtDT(sp.imported_at)}{sp.imported_by && <div className="text-[10px] text-slate-400">{sp.imported_by}</div>}</td>
                    <td className="pr-3">{sp.company_name}</td>
                    <td className="pr-3">
                      <div className="font-medium">{sp.identifier_type} {sp.identifier}</div>
                      <div className="text-[11px] text-slate-400">{sp.commodity === "POWER" ? "Power" : sp.connection_type === "TRANSPORT" ? "Gas · allaccio diretto trasporto" : "Gas · distribuzione"}</div>
                    </td>
                    <td className="pr-3">{sp.remi || "—"}</td>
                    <td className="pr-3 font-semibold">{sp.year}</td>
                    <td className="pr-3 text-xs">{sp.months_actual} / {sp.months_invoiced}</td>
                    <td className="pr-3 text-[11px] text-slate-500 max-w-[220px] truncate" title={sp.filename}>{sp.filename || "—"}</td>
                    <td className="pr-3">{sp.commodity === "POWER" ? <PowerCell sp={sp} onSaved={onChanged} /> : "—"}</td>
                    <td className="pr-3 whitespace-nowrap"><EaVerifyLink companyId={sp.company_id} year={sp.year} compact testid={`ea-sp-verify-${sp.row_id}`} /></td>
                  </tr>
                ))}
                {!list.length && <tr><td colSpan={9} className="py-4 text-center text-xs text-slate-400">Nessun risultato con i filtri selezionati</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EaSupplyPoints;
